import { cn } from '@/lib/utils';

export type LogoSize = 'sm' | 'md' | 'lg';

type LogoProps = {
  className?: string;
  withWordmark?: boolean;
  size?: LogoSize;
};

/** Tight-cropped asset — generated once from
 *  `infratailors-logo-transparent.png` by clipping to the alpha bounding
 *  box and re-padding to a square. The original PNG carried ~30% of
 *  transparent padding on every side, which made the triangle look small
 *  and pushed huge visible gaps between "Infr" and "tailors". This
 *  variant lets the triangle fill its layout box, so the dominant brand
 *  mark reads as the "A" in the `InfrAtailors` lockup without CSS hacks. */
const LOGO_SRC = '/infratailors-logo-tight.png';

const SIZES: Record<LogoSize, { text: string; img: string; mx: string }> = {
  sm: { text: 'text-base', img: 'h-8 w-8', mx: 'mx-px' },
  md: { text: 'text-xl', img: 'h-11 w-11', mx: 'mx-px' },
  // `lg` powers the dashboard sidebar header (h-16). The image is ~2×
  // the text cap height so the triangle still dominates the wordmark
  // without overwhelming it, with 8px of vertical breathing room inside
  // the header. `mx-0` keeps the surrounding letters glued to the
  // triangle's edges now that the tight-cropped asset has no transparent
  // padding to push them away.
  lg: { text: 'text-2xl', img: 'h-12 w-12', mx: 'mx-0' },
};

/**
 * Brand mark — uses the legacy `Infr[triangle]tailors` lockup where the
 * fuchsia-gradient triangle replaces the "A" inline with the wordmark.
 * The letters are set in `font-brand` at regular weight, as in the master
 * logo file and the web site's logo; don't make them bold or tighten them.
 */
export function Logo({ className, withWordmark = true, size = 'md' }: LogoProps) {
  const s = SIZES[size];

  if (!withWordmark) {
    return (
      <img
        src={LOGO_SRC}
        alt="Infratailors logo"
        className={cn(s.img, 'object-contain', className)}
      />
    );
  }

  return (
    <div
      className={cn(
        'inline-flex select-none items-center font-brand font-normal leading-none',
        s.text,
        className,
      )}
    >
      <span>Infr</span>
      <img
        src={LOGO_SRC}
        alt="Infratailors logo"
        className={cn(s.img, s.mx, 'object-contain')}
      />
      <span>tailors</span>
    </div>
  );
}
